/**
 * 【新需求89 子任务1】轻量级中文 -> 拼音工具
 *
 * 目的：让学生列表搜索框支持拼音搜索，例如：
 *   输入 "zhang"  → 命中"张"姓学生
 *   输入 "san"    → 命中名字含"三"的学生
 *   输入 "zs"     → 命中"张三"（首字母简拼）
 *
 * 设计取舍：
 *   - 不引入第三方依赖（如 pinyin-pro），保持构建产物体积稳定
 *   - 字典聚焦于"中国姓氏 + 常见人名汉字"，足以覆盖学生姓名场景
 *   - 字典外的汉字会被原样保留（仍可走中文模糊匹配）
 *
 * 字典覆盖范围：
 *   - 百家姓常见姓氏（约 250 个）
 *   - 常用人名汉字（约 1700 个，HSK 1-6 级核心字 + 高频姓名用字）
 *
 *   * 同一汉字若有多个读音，统一取最常见的人名读音（如"乐"→le，"长"→chang，"重"→zhong）
 *   * 这是"够用即可"的简化方案，不追求 100% 覆盖
 *
 * 公开 API：
 *   toPinyin(str)              -> 全拼字符串，如 "张三" -> "zhangsan"
 *   toPinyinInitials(str)      -> 首字母字符串，如 "张三" -> "zs"
 *   matchByPinyin(text, query) -> 布尔值，判断 text 是否能由 query（拼音/首字母/中文）命中
 */

// ─── 拼音字典（按拼音首字母分组，方便后续维护） ─────────────────────────────
// 每条记录格式: 'pinyin: 汉字汉字汉字'
// 注意：多音字仅取最常见的人名读音；优先满足"姓 + 名"识别
const PINYIN_TABLE_RAW = [
  // A
  'a:阿啊',
  'ai:艾爱埃哀蔼隘碍癌',
  'an:安岸按案暗鞍俺',
  'ang:昂',
  'ao:奥傲澳熬鳌敖凹',
  // B
  'ba:巴芭把八吧霸罢爸跋灞拔',
  'bai:白百柏拜佰摆败',
  'ban:班般板版半伴扮颁',
  'bang:邦帮榜膀绑棒',
  'bao:包宝保暴抱报豹堡饱鲍',
  'bei:贝北倍备背杯被悲辈碑',
  'ben:本奔笨苯',
  'beng:崩绷蹦',
  'bi:比毕笔必币壁碧闭弊蔽避鼻彼璧逼鄙',
  'bian:边变便编辨辩鞭扁卞',
  'biao:表标彪镖飙',
  'bie:别',
  'bin:宾彬斌滨缤殡',
  'bing:冰兵丙秉炳病并',
  'bo:波博伯泊勃柏帛搏卜驳簿钵渤',
  'bu:不步布部补埔卜',
  // C
  'cai:才材财蔡采菜彩裁',
  'can:参残灿惨蚕餐',
  'cang:仓苍藏沧',
  'cao:草曹槽操',
  'ce:策册测厕',
  'cen:岑',
  'ceng:层曾',
  'cha:查察叉差插茶',
  'chai:柴差拆豺',
  'chan:产单缠铲蝉禅',
  'chang:常长场昌厂偿尝唱倡',
  'chao:朝超抄潮巢炒',
  'che:车彻撤扯',
  'chen:陈晨臣辰沉尘陈衬称谌忱',
  'cheng:成程城承诚乘称澄丞橙惩',
  'chi:吃池迟持赤齿尺斥痴翅匙耻',
  'chong:重虫充冲崇宠',
  'chou:愁仇丑筹绸抽稠',
  'chu:出初处楚储除厨触雏础楚',
  'chuai:揣',
  'chuan:川传船穿喘',
  'chuang:窗床创闯',
  'chui:吹炊垂锤',
  'chun:春纯醇唇蠢淳',
  'chuo:绰戳',
  'ci:次词慈瓷此辞磁雌刺赐',
  'cong:从聪丛葱匆',
  'cou:凑',
  'cu:粗醋促簇',
  'cuan:窜',
  'cui:崔催翠脆萃',
  'cun:村寸存',
  'cuo:错措搓',
  // D
  'da:大达打答搭瘩',
  'dai:代戴待呆带袋贷怠',
  'dan:单旦丹但担胆诞淡蛋弹',
  'dang:党当档挡荡',
  'dao:道导刀岛倒到稻盗悼',
  'de:德得',
  'deng:邓登灯等瞪凳',
  'di:迪低弟底地帝第敌堤滴笛抵蒂',
  'dian:点电典店殿垫淀',
  'diao:刁雕掉吊钓调',
  'die:跌叠蝶碟',
  'ding:丁顶定订盯锭',
  'diu:丢',
  'dong:东动冬董懂栋洞',
  'dou:都斗豆抖陡兜',
  'du:都读度独杜毒督堵渡赌妒睹',
  'duan:段断短端缎',
  'dui:对队堆兑',
  'dun:顿盾蹲钝盹',
  'duo:多朵躲堕剁夺舵惰',
  // E
  'e:鹅额俄恶饿厄峨蛾扼',
  'en:恩',
  'er:儿尔而二饵耳',
  // F
  'fa:发法罚乏伐',
  'fan:范凡反饭翻番繁烦帆贩犯泛',
  'fang:方房放访防芳仿纺',
  'fei:飞非肥费废沸匪扉妃菲',
  'fen:分粉份纷奋愤芬氛吩坟',
  'feng:风峰丰封冯枫蜂凤奉缝讽逢',
  'fo:佛',
  'fou:否',
  'fu:夫福付服府父富副复腐扶幅符付辅赋俯腹覆抚浮敷',
  // G
  'ga:嘎',
  'gai:该改概盖钙',
  'gan:甘干赣敢感肝杆赶橄',
  'gang:刚岗钢港纲',
  'gao:高告糕搞稿膏皋',
  'ge:格哥歌革各葛戈隔阁鸽割',
  'gei:给',
  'gen:根跟',
  'geng:更耕庚埂',
  'gong:工公共功宫巩贡攻供恭龚拱',
  'gou:够沟苟构购钩狗勾',
  'gu:古顾故谷骨股鼓姑固孤辜雇估贾',
  'gua:挂瓜寡刮',
  'guai:怪乖拐',
  'guan:关官管观馆冠贯灌',
  'guang:光广逛',
  'gui:贵桂规归鬼龟跪柜诡轨闺硅',
  'gun:滚棍',
  'guo:国果过郭锅裹',
  // H
  'ha:哈',
  'hai:海还害孩亥骸',
  'han:韩寒汉含函喊翰憾涵旱罕',
  'hang:航杭行',
  'hao:好号毫豪浩昊郝皓壕嚎耗',
  'he:何和河合贺鹤荷盒赫禾褐核呵',
  'hei:黑嘿',
  'hen:很恨痕狠',
  'heng:衡横恒亨',
  'hong:红洪宏鸿弘虹哄烘',
  'hou:后侯厚候喉猴吼',
  'hu:胡湖虎户呼护互忽糊壶狐弧蝴沪',
  'hua:华花化划话画桦哗',
  'huai:怀淮坏槐',
  'huan:欢还环换患幻焕缓唤涣',
  'huang:黄皇荒慌煌惶恍簧',
  'hui:回会汇辉惠灰挥徽毁悔慧晖讳贿',
  'hun:婚魂浑混昏',
  'huo:火活或货获祸惑霍',
  // J
  'ji:吉积纪季计极及己急集寄技际既济激集机几记济基继挤激急疾即籍稽冀剂迹击姬肌肌',
  'jia:家加佳贾甲价假架嘉夹颊',
  'jian:坚见间检建简健剑监减坚兼鉴渐键箭尖肩奸柬笺溅僭',
  'jiang:江将姜讲蒋强降浆奖匠犟',
  'jiao:交教叫脚焦角较骄郊娇胶椒矫',
  'jie:接街解结杰节阶届皆姐戒洁竭借介劫芥',
  'jin:金近今进劲津紧禁仅锦谨晋瑾尽斤筋',
  'jing:京经井景静敬境精竞警镜净竟惊径',
  'jiong:窘炯',
  'jiu:就九旧酒久救究纠舅',
  'ju:句巨菊矩举具据居局聚剧拒沮鞠',
  'juan:卷捐绢娟眷',
  'jue:决觉绝倔崛掘爵嚼',
  'jun:军君均俊菌竣骏峻',
  // K
  'ka:卡咖喀',
  'kai:开凯慨楷',
  'kan:看砍刊勘堪坎',
  'kang:康抗扛慷亢',
  'kao:考靠拷烤',
  'ke:可科客课克柯科颗渴壳棵咳苛刻',
  'ken:肯啃垦恳',
  'keng:坑',
  'kong:孔空控恐',
  'kou:口扣寇',
  'ku:库哭酷苦窟枯',
  'kua:夸跨垮',
  'kuai:快块筷',
  'kuan:宽款',
  'kuang:狂矿况框旷邝',
  'kui:奎魁愧馈葵',
  'kun:昆坤捆',
  'kuo:扩括阔廓',
  // L
  'la:拉啦腊辣蜡喇',
  'lai:来赖莱',
  'lan:兰蓝懒览拦栏揽烂滥',
  'lang:浪郎朗廊狼',
  'lao:老劳牢姥唠涝',
  'le:乐了勒',
  'lei:雷类磊累泪垒蕾',
  'leng:冷',
  'li:李立利里历力礼丽离厉励梨理黎莉俐荔粒篱栗笠隶',
  'lian:连联莲廉怜炼练帘恋脸链镰',
  'liang:良凉两梁亮粱量谅',
  'liao:辽了廖料聊疗僚撩寥',
  'lie:列烈猎裂劣',
  'lin:林临邻琳麟磷淋鳞遴',
  'ling:玲零灵铃陵岭领令凌龄羚',
  'liu:刘流六留柳琉硫陆',
  'long:龙隆笼咙聋陇',
  'lou:楼漏陋娄',
  'lu:陆路鲁鹿露卢禄录炉芦碌庐颅',
  'lv:吕旅律绿驴铝侣',
  'luan:乱卵峦銮',
  'lue:略',
  'lun:论轮伦仑',
  'luo:罗洛落骆螺锣',
  // M
  'ma:马麻妈骂玛码蚂',
  'mai:买卖麦迈脉',
  'man:满慢漫蛮馒瞒鳗曼',
  'mang:忙茫莽盲',
  'mao:毛冒貌矛茂猫帽',
  'mei:美没每枚梅媒煤眉妹魅霉',
  'men:门们闷',
  'meng:孟蒙梦盟猛',
  'mi:米密迷弥蜜秘觅',
  'mian:面免棉绵勉',
  'miao:苗秒妙庙描',
  'mie:灭蔑',
  'min:民敏闽悯岷',
  'ming:明名命鸣铭',
  'miu:谬',
  'mo:墨末莫摩魔默磨膜抹漠寞',
  'mou:某谋',
  'mu:木目母牧穆幕暮慕睦',
  // N
  'na:那拿娜呐纳',
  'nai:奶耐乃柰',
  'nan:南难男',
  'nang:囊',
  'nao:闹脑恼挠',
  'ne:呢',
  'nei:内',
  'nen:嫩',
  'neng:能',
  'ni:你尼倪泥逆腻拟',
  'nian:年念粘碾',
  'niang:娘酿',
  'niao:鸟',
  'nie:聂涅捏',
  'nin:您',
  'ning:宁凝拧',
  'niu:牛纽扭妞',
  'nong:农浓弄',
  'nu:奴努怒',
  'nuan:暖',
  'nuo:诺挪',
  // O
  'ou:欧偶藕',
  // P
  'pa:怕爬扒帕',
  'pai:派牌排拍',
  'pan:潘盼判攀盘磐',
  'pang:庞胖旁',
  'pao:抛跑泡袍刨',
  'pei:陪赔配培裴佩沛',
  'pen:盆喷',
  'peng:朋鹏彭蓬碰捧棚膨',
  'pi:皮披辟批屁啤匹疲',
  'pian:篇片骗偏',
  'piao:飘票漂瓢',
  'pie:撇',
  'pin:品贫拼频聘',
  'ping:平评凭瓶苹屏',
  'po:破颇婆魄迫',
  'pu:普扑铺谱朴蒲匍',
  // Q
  'qi:其七齐起气棋期奇骑旗企汽弃契泣戚漆栖凄岂祁祺崎',
  'qia:洽恰',
  'qian:钱前千迁签牵谦欠浅遣潜谴铅虔',
  'qiang:强抢墙腔枪羌呛',
  'qiao:乔桥巧侨敲俏瞧悄翘窍',
  'qie:切且窃茄怯',
  'qin:秦琴勤亲钦芹寝沁覃',
  'qing:青清庆轻情请晴卿氢',
  'qiong:穷琼',
  'qiu:秋求球丘邱仇囚泅',
  'qu:区曲取去屈趣渠驱躯娶',
  'quan:全权劝券泉拳犬',
  'que:确缺却雀鹊',
  'qun:群裙',
  // R
  'ran:然燃染冉',
  'rang:让壤嚷',
  'rao:绕饶扰',
  're:热',
  'ren:任人仁忍认刃韧',
  'reng:仍',
  'ri:日',
  'rong:荣容融蓉溶绒熔戎',
  'rou:柔肉揉',
  'ru:如入儒辱乳茹',
  'ruan:阮软',
  'rui:瑞锐睿芮蕊',
  'run:润',
  'ruo:若弱',
  // S
  'sa:撒洒萨仨',
  'sai:塞赛',
  'san:三散伞',
  'sang:桑丧嗓',
  'sao:扫嫂骚',
  'se:色塞涩',
  'sen:森',
  'seng:僧',
  'sha:沙杀纱傻啥',
  'shai:筛晒',
  'shan:山善单衫闪扇陕珊删杉煽',
  'shang:商上尚伤赏裳',
  'shao:邵少绍稍烧梢哨',
  'she:社设射蛇舌摄涉舍赦',
  'shen:沈申深神身审甚慎肾参渗绅',
  'sheng:生声升盛圣胜剩省牲甥',
  'shi:石市世史诗时识使始式驶仕士适示侍释释湿狮饰拭逝氏弑屎',
  'shou:手收受寿首授瘦守兽',
  'shu:书数树叔术输熟舒疏淑述署蜀束鼠暑墅薯',
  'shua:刷',
  'shuai:衰摔甩帅',
  'shuan:栓拴',
  'shuang:双爽霜',
  'shui:水谁睡税',
  'shun:顺瞬舜',
  'shuo:说硕',
  'si:四思司私死丝寺斯似肆撕嗣祀饲',
  'song:松宋送颂诵嵩耸',
  'sou:搜艘嗽',
  'su:苏诉素速塑肃宿俗粟',
  'suan:算酸蒜',
  'sui:随岁虽碎隋遂髓',
  'sun:孙损笋荪',
  'suo:所索锁缩梭嗦',
  // T
  'ta:他她它塔踏塌獭',
  'tai:太台泰抬态胎',
  'tan:谭谈坦探滩弹叹炭潭',
  'tang:唐堂糖塘膛汤躺',
  'tao:套讨陶涛逃桃淘',
  'te:特',
  'teng:腾疼藤',
  'ti:体提替题剃涕梯踢蹄',
  'tian:田天添填甜',
  'tiao:条跳挑调',
  'tie:铁贴帖',
  'ting:听亭庭停廷挺艇',
  'tong:同通统童铜筒桶痛桐瞳',
  'tou:头投透偷',
  'tu:土图突徒涂屠途凸吐',
  'tuan:团',
  'tui:推退腿蜕',
  'tun:吞屯臀',
  'tuo:拓托脱拖驼椭妥',
  // W
  'wa:挖娃瓦袜',
  'wai:外',
  'wan:万完晚湾婉宛碗弯腕玩顽',
  'wang:王往望忘网亡旺汪',
  'wei:卫位为味未围维伟唯威危微违韦委炜慰魏喂尾纬畏蔚',
  'wen:文温问闻稳吻纹',
  'weng:翁',
  'wo:我握窝沃卧渥',
  'wu:吴武五午无伍物务雾舞乌污巫吾屋戊伍捂',
  // X
  'xi:习西希席洗息夕析喜溪锡熙稀息悉膝晰惜熄犀羲奚',
  'xia:夏下吓厦霞峡狭瑕侠',
  'xian:先现县显贤献鲜限弦闲咸纤宪羡线馅陷',
  'xiang:向乡香想象项祥相详响享湘翔巷襄',
  'xiao:小晓肖效笑销孝萧霄哮宵',
  'xie:谢写些斜协解谐携泄械胁屑邪鞋蟹',
  'xin:新心信欣辛馨锌薪芯',
  'xing:行兴形姓星型醒幸刑兄杏',
  'xiong:雄熊兄凶胸',
  'xiu:修秀羞袖宿绣朽',
  'xu:许续序徐绪续叙旭须虚畜蓄',
  'xuan:宣选玄轩萱炫旋眩喧悬',
  'xue:学雪血薛穴削',
  'xun:训寻巡讯驯循殉迅旬熏勋',
  // Y
  'ya:亚雅压牙呀鸭哑芽崖涯',
  'yan:严言研沿炎延烟岩盐颜雁严宴雁眼演掩堰焰焉沿沿燕谚',
  'yang:杨阳样养氧仰扬羊洋央秧痒',
  'yao:要药摇姚遥腰耀邀谣遥窑咬',
  'ye:也业叶夜野页爷耶冶',
  'yi:一以亿义意议易已医依疑遗艺译易移异益翼亿伊宜怡仪谊倚邑彝抑挹翊艺奕',
  'yin:因银印音引饮阴隐尹寅淫',
  'ying:英应营赢影迎硬映颖盈鹰荧莹萤婴樱',
  'yo:哟',
  'yong:用永勇拥涌泳庸雍咏',
  'you:有由又游优友尤幼忧右犹幽悠铀邮',
  'yu:于鱼雨与玉余预语育遇愈寓郁愚舆裕渔禹屿俞庾御逾娱榆虞瑜豫俞',
  'yuan:元远员园院原源缘圆援越愿怨袁苑垣猿渊',
  'yue:月乐越约阅跃悦岳',
  'yun:云运允韵孕匀晕蕴芸耘',
  // Z
  'za:杂砸咋',
  'zai:在再载灾栽宰',
  'zan:咱赞暂攒',
  'zang:藏脏葬赃',
  'zao:早造遭糟燥灶皂凿躁噪',
  'ze:则责泽择',
  'zei:贼',
  'zen:怎',
  'zeng:曾增赠憎',
  'zha:扎查炸渣眨札榨乍',
  'zhai:宅斋摘窄债',
  'zhan:展占战站詹粘斩湛沾',
  'zhang:张章长涨掌账丈帐仗胀彰',
  'zhao:赵照召找昭朝罩兆肇',
  'zhe:这者着哲折浙遮辙蔗',
  'zhen:真镇珍枕震阵贞针诊振朕',
  'zheng:正政证整郑征争挣症蒸',
  'zhi:之只知至志制治智值职指直枝芝纸织志置质致殖滞稚秩芷',
  'zhong:中重种钟众终忠衷肿仲',
  'zhou:州周洲舟皱粥昼宙骤',
  'zhu:朱主住注助祝著竹珠诸竺铸柱株猪驻铸贮筑逐瞩',
  'zhua:抓',
  'zhuan:专转赚砖传撰',
  'zhuang:庄装壮状妆撞',
  'zhui:追坠缀',
  'zhun:准',
  'zhuo:卓桌捉浊酌琢茁',
  'zi:子自字资姿紫滋籽兹仔咨孜梓',
  'zong:总宗踪综纵棕',
  'zou:走奏邹揍',
  'zu:祖足族阻组租',
  'zuan:钻',
  'zui:最罪嘴醉',
  'zun:尊遵樽',
  'zuo:作做坐左昨座佐凿',
];

// 反向映射: 汉字 -> 拼音（构建一次，运行时常量级查找）
const CHAR_TO_PINYIN = (() => {
  const map = new Map();
  for (const line of PINYIN_TABLE_RAW) {
    const idx = line.indexOf(':');
    if (idx <= 0) continue;
    const py = line.slice(0, idx);
    const chars = line.slice(idx + 1);
    for (const ch of chars) {
      // 同字若已存在，保留先注册版本（首音通常是更常见读音）
      if (!map.has(ch)) map.set(ch, py);
    }
  }
  return map;
})();

/**
 * 把字符串转换为全拼小写（连写，不加分隔符）。
 * 字典外的字符（含字母数字）原样保留，并统一转小写。
 */
export const toPinyin = (str) => {
  if (!str) return '';
  let out = '';
  for (const ch of String(str)) {
    const py = CHAR_TO_PINYIN.get(ch);
    if (py) {
      out += py;
    } else {
      out += ch.toLowerCase();
    }
  }
  return out;
};

/**
 * 把字符串转换为拼音首字母（小写连写）。
 * 字典外字符的"首字符"也会被纳入（保持简单）。
 */
export const toPinyinInitials = (str) => {
  if (!str) return '';
  let out = '';
  for (const ch of String(str)) {
    const py = CHAR_TO_PINYIN.get(ch);
    if (py) {
      out += py[0];
    } else {
      const lc = ch.toLowerCase();
      // 仅保留 a-z 0-9 作为首字母拼接，避免空格、标点污染
      if (/[a-z0-9]/.test(lc)) out += lc;
    }
  }
  return out;
};

/**
 * 综合匹配：判断 text（如学生姓名）是否能由 query 命中。
 *
 * 命中规则（OR）：
 *   1. 原文（中文/任意）小写包含 query
 *   2. 全拼（如"zhangsan"）包含 query
 *   3. 首字母简拼（如"zs"）包含 query
 *
 * 空 query 视为匹配。
 */
export const matchByPinyin = (text, query) => {
  if (!query) return true;
  if (!text) return false;
  const q = String(query).trim().toLowerCase();
  if (!q) return true;
  const raw = String(text).toLowerCase();
  if (raw.includes(q)) return true;
  // 仅当 query 是 ASCII 字母/数字时才走拼音匹配，否则没必要
  if (!/^[a-z0-9]+$/.test(q)) return false;
  if (toPinyin(text).includes(q)) return true;
  if (toPinyinInitials(text).includes(q)) return true;
  return false;
};

export default { toPinyin, toPinyinInitials, matchByPinyin };
